// ⇧T: every theme in one strip along the bottom. Arrows try each one on the
// real board as you go, typing narrows the strip, Enter keeps the one you are
// on, Esc puts the board back as it was. One pass to find the right look.

import { useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Search, Wand2 } from "lucide-react";
import { PALETTES } from "../../shared/palettes";
import { PICTURE_THEMES } from "../../shared/pictureThemes";
import { currentBoard, get, set, useStore } from "../state/store";
import { wearTheme } from "../state/themes";
import { THEMES, themeDef } from "../themes/themes";
import { ThemeCard } from "../themes/ThemeCard";

interface Entry {
  id: string;
  group: string;
}

export function ThemePicker() {
  const open = useStore((s) => s.themePicker);
  if (!open) return null;
  return <Strip />;
}

function Strip() {
  const made = useStore(useShallow((s) => Object.values(s.themes)));
  const board = useStore(currentBoard);
  const worn = board?.theme.custom ?? board?.theme.id ?? "meadow";
  const [q, setQ] = useState("");
  const all = useMemo<Entry[]>(() => [...made.map((t) => ({ id: t.id, group: "Yours" })), ...PICTURE_THEMES.map((p) => ({ id: p.id, group: "Photographs and paintings" })), ...PALETTES.map((p) => ({ id: p.id, group: "Popular color schemes" })), ...THEMES.map((t) => ({ id: t.id, group: "Hand-drawn places" }))], [made]);
  const list = useMemo(() => narrow(all, q), [all, q]);
  // The strip is on a theme, not a place in the row: it opens on the one the board wears, so opening
  // it changes nothing, and a theme made or removed while it is open moves nothing either.
  const [pick, setPick] = useState<string | null>(worn);
  const i = list.findIndex((e) => e.id === pick);
  const strip = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const placed = useRef(false);
  // the strip glides to the card the keys or the search move to; a card pointed at is already in view
  const follow = useRef(true);
  // where the pointer last was, and the card it last moved onto: the strip opening under a pointer at
  // rest, or sliding a card under it, sends that card a pointerenter all the same
  const pointer = useRef<{ x: number; y: number; on: string | null } | null>(null);
  const at = list[i] ?? null;

  const close = (keep: boolean) => {
    if (keep && at && at.id !== worn) wearTheme(at.id);
    set({ themePicker: false, themePreview: null });
  };

  // the board wears whatever the strip is on, and only while it is open: a try-on still on its way as
  // Esc closes it must not land after
  useEffect(() => {
    if (get().themePicker) set({ themePreview: at && at.id !== worn ? at.id : null });
  }, [at?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => set({ themePreview: null }), []);

  useEffect(() => {
    input.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        step(1);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        step(-1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        closeRef.current(true);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeRef.current(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  const listRef = useRef(list);
  listRef.current = list;
  const closeRef = useRef(close);
  closeRef.current = close;

  /** The keys and the search move the strip; the pointer counts again only once it moves. */
  const steer = () => {
    follow.current = true;
    if (pointer.current) pointer.current.on = null;
  };
  /** One along the row, or the first when the strip is on none. */
  const step = (by: 1 | -1) => {
    steer();
    setPick((p) => {
      const l = listRef.current;
      const n = l.findIndex((e) => e.id === p);
      return l[Math.max(0, Math.min(n + by, l.length - 1))]?.id ?? p;
    });
  };
  // a new search starts on its first match; no search, back on the one the board wears
  const search = (text: string) => {
    steer();
    setQ(text);
    setPick(text.trim() ? (narrow(all, text)[0]?.id ?? null) : worn);
  };
  /** Puts the strip on a card where it is, without gliding it out from under the pointer. */
  const here = (id: string) => {
    follow.current = false;
    setPick(id);
  };
  /** A card the pointer moved onto, or that Tab reached, is tried on. */
  const point = (id: string) => {
    const tabbed = document.activeElement?.closest<HTMLElement>(".tpick-item")?.dataset.id === id;
    if (pointer.current?.on === id || tabbed) here(id);
  };
  /** Only a change of place is a move; the first word from the pointer says where it rests. */
  const moved = (e: React.PointerEvent) => {
    const was = pointer.current;
    const card = (e.target as Element).closest<HTMLElement>(".tpick-item")?.dataset.id ?? null;
    const real = !!was && (was.x !== e.clientX || was.y !== e.clientY);
    pointer.current = { x: e.clientX, y: e.clientY, on: real ? card : (was?.on ?? null) };
  };
  // keep the one in focus in view: there at once as it opens, then gliding as the keys move it
  useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>(`[data-at="${i}"]`);
    if (!el || !follow.current) return;
    el.scrollIntoView({ inline: "center", block: "nearest", behavior: placed.current ? "smooth" : "instant" });
    placed.current = true;
  }, [i, list]);

  // the strip in its groups, each card keeping its place in the arrow order
  const groups: { group: string; entries: { e: Entry; n: number }[] }[] = [];
  list.forEach((e, n) => {
    const g = groups[groups.length - 1];
    if (g && g.group === e.group) g.entries.push({ e, n });
    else groups.push({ group: e.group, entries: [{ e, n }] });
  });
  return (
    <div className="tpick" onPointerDown={(e) => e.target === e.currentTarget && close(false)} onPointerMove={moved}>
      <div className="tpick-panel island" onPointerDown={(e) => e.stopPropagation()}>
        <div className="tpick-head">
          <Search size={14} />
          <input ref={input} value={q} onChange={(e) => search(e.target.value)} placeholder="Type to find a theme" aria-label="Find a theme" />
          <span className="tpick-keys">
            <kbd className="kbd">← →</kbd> try on <kbd className="kbd">↵</kbd> keep <kbd className="kbd">Esc</kbd> back
          </span>
        </div>
        <div className="tpick-strip" ref={strip} data-scroll>
          <button
            className="tpick-make"
            onClick={() => {
              set({ themePicker: false, themePreview: null, panel: "theme", panelArg: { make: true, idea: q.trim() } });
            }}
          >
            <Wand2 size={18} />
            <b>{q.trim() ? `Make “${q.trim()}”` : "Make your own"}</b>
            <i>Describe it, and it is made for you</i>
          </button>
          {groups.map((g) => (
            <div key={g.group} className="tpick-sec">
              <span className="tpick-group">{g.group}</span>
              <div className="tpick-row">
                {g.entries.map(({ e, n }) => (
                  <div key={e.id} className="tpick-item" data-at={n} data-id={e.id}>
                    <ThemeCard id={e.id} big on={n === i} onPick={() => (n === i ? close(true) : here(e.id))} onPreview={() => point(e.id)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!list.length && <p className="tpick-none">No theme matches “{q}” yet. Make it with the card on the left.</p>}
        </div>
      </div>
    </div>
  );
}

/** The themes whose name, blurb, maker or group hold every word typed. */
function narrow(all: Entry[], q: string): Entry[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return all;
  return all.filter((e) => {
    const d = themeDef(e.id);
    const hay = `${d?.name ?? ""} ${d?.blurb ?? ""} ${d?.custom?.by ?? ""} ${e.group}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Opens the strip, in place of the panel open, unless that holds words someone typed and has not saved (the store's guard keeps those). Full screen too, as on the board. */
export function openThemePicker() {
  set({ themePicker: true, panel: null });
}
